// A change card (spec §13.10, §13.15): what the coach (or the athlete)
// changed, in the athlete's terms. The body is render-ready (the backend's
// app/thread/cards.py): a title, before → after per session (day, title,
// sport, minutes, why), the reason, and whether it can be undone. Expanded for
// 24 h after it was made, then one compact line that opens on a tap. Undo
// (POST /v2/changes/{refs.change_id}/undo) while active and undoable; Why?
// opens the composer about this card.
//
// A session-level proposal (an availability note's "Plan around your
// note?", or the same card shape for sessions) uses the same layout with the
// accent border and Accept · Not now · Why?, expanded until answered. An
// accept the coach answered in words (`reply`) shows them (ProposalStatus).
// One the calendar changed under (body.stale) says so and offers Refresh
// instead of Accept (threadView.staleNote).
import React, { useState } from 'react';
import { AlertTriangle, ArrowRight, Check, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '../ds';
import { CardActions, CoachCard, Eyebrow, WhyLink } from './coachCard';
import { cn } from '../../lib/utils';
import { SportIcon } from '../../lib/sportIcons';
import { commands } from '../../v2/commands';
import { changeView, sessionLine } from '../../v2/threadView';
import ProposalStatus from './ProposalStatus';
import HeldNote from './HeldNote';
import { answersAfter } from '../../v2/failures';

const CHANGE_OUTCOME = { undone: 'Undone', superseded: 'Replaced by a later change', accepted: 'Accepted', dismissed: 'Dismissed' };

function SessionRow({ before, after }) {
  const one = after || before;
  const why = (after || before)?.why;
  return (
    <li className="flex items-start gap-2 py-1">
      <SportIcon sport={one?.sport} className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
      <div className="min-w-0 flex-1 type-caption text-text-body">
        {before && after ? (
          <p className="m-0 flex flex-wrap items-center gap-1">
            <span className="text-text-tertiary line-through">{sessionLine(before)}</span>
            <ArrowRight size={12} className="shrink-0 text-text-tertiary" aria-label="becomes" />
            <span className="font-medium text-text-primary">{sessionLine(after)}</span>
          </p>
        ) : before ? (
          <p className="m-0"><span className="mr-1 font-medium text-text-secondary">Removed</span><span className="text-text-tertiary line-through">{sessionLine(before)}</span></p>
        ) : (
          <p className="m-0"><span className="mr-1 font-medium text-text-secondary">Added</span><span className="font-medium text-text-primary">{sessionLine(after)}</span></p>
        )}
        {why && <p className="m-0 mt-0.5 type-caption text-text-secondary">Why: {why}</p>}
      </div>
    </li>
  );
}

/** `readOnly` (a coach viewing the athlete): the card without Accept, Not now, Undo or Why?. */
export default function ChangeCard({ item, onAct, onWhy, pending = [], now = Date.now(), readOnly = false, reply = null }) {
  const v = changeView(item, { now, pending });
  const after = answersAfter(reply);
  const offers = (a) => v.canAnswer && v.actions.includes(a) && (!after || after.includes(a));
  const isProposal = item.type === 'proposal';
  const [open, setOpen] = useState(null);         // null: follow the rule (24 h; a proposal until answered)
  const expanded = open ?? v.expanded;
  const outcome = item.state === 'active' ? null : (isProposal ? v.outcome : CHANGE_OUTCOME[item.state] || item.state);
  const waiting = isProposal && item.state === 'active';
  const replaced = item.state === 'superseded';
  // Figma: SUGGESTION (teal) while it waits; CHANGED · ACCEPTED once done; REPLACED BY A LATER CHANGE, struck through.
  const eyebrow = replaced ? (isProposal ? outcome : CHANGE_OUTCOME.superseded)
    : [isProposal ? 'Suggestion' : 'Changed', outcome].filter(Boolean).join(' · ');

  return (
    <CoachCard tone={waiting ? 'accent' : 'plain'} role="article"
      aria-label={`${isProposal ? 'Suggestion' : 'Change'}: ${v.title}${outcome ? `, ${outcome}` : ''}`}
      className={cn(waiting && 'border-accent-default')}>
      {expanded && <Eyebrow tone={waiting ? 'accent' : 'muted'}>{eyebrow}</Eyebrow>}
      <button type="button" onClick={() => setOpen(!expanded)} aria-expanded={expanded}
        className="-my-1 flex min-h-[28px] w-full items-center gap-2 text-left">
        <span className={cn('flex-1', replaced ? 'type-body-small text-text-tertiary line-through' : 'type-label-section text-text-primary',
          !expanded && 'truncate')}>{v.title}</span>
        {!expanded && outcome && <span className="inline-flex shrink-0 items-center gap-1 type-caption text-text-tertiary"><Check size={12} /> {outcome}</span>}
        {expanded ? <ChevronDown size={14} className="shrink-0 text-text-tertiary" /> : <ChevronRight size={14} className="shrink-0 text-text-tertiary" />}
      </button>
      {expanded && (
        <>
          {v.rows.length > 0 && <ul className="m-0 list-none divide-y divide-border-subtle p-0">{v.rows.map((r) => <SessionRow key={r.key} before={r.before} after={r.after} />)}</ul>}
          {v.counts && <p className="m-0 type-caption text-text-tertiary">Sessions: {v.counts}</p>}
          {v.reason && <p className="m-0 type-caption text-text-secondary">{v.reason}</p>}
          {v.stale && (
            <p className="m-0 flex items-start gap-1.5 rounded-chip bg-bg-raised px-2.5 py-1.5 type-caption text-zone-threshold" role="status">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden="true" /> {v.stale}
            </p>
          )}
          <HeldNote item={item} />
          {isProposal && <ProposalStatus outcome={reply} />}
          {!readOnly && (
          <CardActions>
            {offers('refresh') && (
              <Button size="small" icon={<RefreshCw size={13} />} onClick={() => onAct?.(commands.refreshProposal(item))}>Refresh</Button>
            )}
            {offers('accept') && (
              <Button size="small" onClick={() => onAct?.(commands.acceptProposal(item))}>Accept</Button>
            )}
            {offers('dismiss') && (
              <Button size="small" kind="secondary" onClick={() => onAct?.(commands.dismissProposal(item))}>Not now</Button>
            )}
            {v.canUndo && (
              <Button size="small" kind="text" className="px-1" onClick={() => onAct?.(commands.undo(item, v.changeId))}>Undo</Button>
            )}
            {v.acting && <span className="type-caption text-text-tertiary">Sending…</span>}
            <WhyLink onClick={() => onWhy?.(item)} />
          </CardActions>
          )}
        </>
      )}
    </CoachCard>
  );
}
